import { Flex, Spacer, spaces, TabPanel, Text } from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import { AssetDetailsTabs } from '@/pages/Assets/types';
import { AssetEventsHistory } from '@/components/Assets/AssetEventsHistory/AssetEventsHistory';
import { useLazyGetAssetEventsHistoryQuery } from '@/redux/slices/assetApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { AssetEvent } from '@/types/assets';
import { useSearchParams } from 'react-router-dom';
import { AssetEventsHistoryQueryParams } from '@/types/queryParamKeys';
import {
  AssetEventCategory,
  ActionType,
  SortOrder,
} from '@electricjs/core_entity-client';

export const AssetHistoryTab = ({ assetId }: { assetId: string }) => {
  const { t } = useTranslation('assets');
  const [queryParams] = useSearchParams();

  const sortOrder =
    (queryParams.get(AssetEventsHistoryQueryParams.SortOrder) as SortOrder) ||
    SortOrder.Desc;

  const eventTypes = useMemo(
    () => queryParams.getAll(AssetEventsHistoryQueryParams.EventTypes) ?? [],
    [queryParams]
  );
  const categories = useMemo(
    () => queryParams.getAll(AssetEventsHistoryQueryParams.Categories) ?? [],
    [queryParams]
  );

  const organizationId = useGetOrganizationId();
  const [events, setEvents] = useState<AssetEvent[]>([]);
  const [lastDate, setLastDate] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);

  const [getAssetEventsHistory, { isFetching, isLoading: isLoadingTable }] =
    useLazyGetAssetEventsHistoryQuery();

  const { space_l } = spaces;

  useEffect(() => {
    setEvents([]);
    setLastDate(undefined);
    setHasMore(true);
  }, [eventTypes, categories, sortOrder]);

  // used to prevent race condition when fetching data
  const latestReqIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    // increment the request id
    const reqId = ++latestReqIdRef.current;

    const params = {
      organizationId,
      assetId,
      ...(lastDate && { pageCursorDate: lastDate }),
      ...(eventTypes.length > 0 && { actionTypes: eventTypes as ActionType[] }),
      ...(categories.length > 0 && {
        categories: categories as AssetEventCategory[],
      }),
      sortOrder,
    };

    const { data } = await getAssetEventsHistory(params);

    // if the request id is not the latest, ignore the response
    if (reqId !== latestReqIdRef.current) {
      return;
    }

    setEvents(prev => [...prev, ...(data?.assetEvents ?? [])]);
    setLastDate(data?.lastDate ?? undefined);
    setHasMore(!!data?.lastDate);
  }, [
    organizationId,
    assetId,
    lastDate,
    eventTypes,
    categories,
    sortOrder,
    getAssetEventsHistory,
  ]);

  useEffect(() => {
    // fetch data if there are no events and there is more data to fetch
    if (events.length === 0 && hasMore) {
      fetchData();
    }
  }, [events.length, hasMore, isFetching, isLoadingTable, fetchData]);

  const handleLoadMore = async () => {
    // fetch data if there is more data to fetch and not fetching
    if (isFetching || !hasMore || events.length === 0) return;

    await fetchData();
  };

  return (
    <TabPanel id={AssetDetailsTabs.History} minHeight="600px">
      <Spacer y={space_l} />
      <Flex flexDirection="column" width="100%" gap={space_l}>
        <Text ml="auto" variant="legal">
          {t('assetHistoryTab.mdmEventHistoryLimit')}
        </Text>
        <AssetEventsHistory
          assetId={assetId}
          events={events}
          onLoadMore={handleLoadMore}
          isFetchingMore={isFetching && events.length > 0} // loading for each load more request
          hasMore={hasMore} // prevent infinite loop
          // first request is always loading or if there are no events
          isLoadingTable={isLoadingTable || (isFetching && events.length === 0)}
        />
      </Flex>
    </TabPanel>
  );
};
